import { feeMoneyCents, feeMoneyText, formatFeeAdjustmentAmount } from "../../fee-adjustment";
import { FINANCE_PAYMENT_REQUEST_MAX_ATTACHMENT_BYTES } from "../config";
import type {
  FinanceInvoiceStatus,
  FinancePaymentRecordDraft,
  FinancePaymentRequestDepartment,
  FinancePaymentRequestDetail,
  FinancePaymentRequestDraft,
  FinancePaymentRequestLine,
  FinancePaymentRequestListScope,
  FinancePaymentRequestPayeeAccount,
  FinancePaymentRequestResubmit,
  FinancePaymentRequestRound,
  FinancePaymentRequestRoundStep,
  FinancePaymentRequestStatus,
  FinancePaymentRequestTarget,
  FinancePaymentType,
} from "../types";

const PAYMENT_TYPE_LABELS: Readonly<Record<FinancePaymentType, string>> = {
  balance: "尾款",
  progress: "进度款",
  full: "全款",
  prepayment: "预付款",
};

const INVOICE_STATUS_LABELS: Readonly<Record<FinanceInvoiceStatus, string>> = {
  later: "后补票",
  received: "有票",
  not_required: "无需票",
};

const STATUS_LABELS: Readonly<Record<FinancePaymentRequestStatus, string>> = {
  pending: "审批中",
  approved: "已通过",
  rejected: "已驳回",
  withdrawn: "已撤回",
  paid: "已付",
};

const LIST_SCOPE_LABELS: Readonly<Record<FinancePaymentRequestListScope, string>> = {
  all: "全部",
  awaiting_payment: "待付款",
  mine: "我发起的",
};

/**
 * 请款记录的筛选写进地址：参数名 `scope` 与接口一致，「全部」不写。页面以地址为准，
 * 首次进入、刷新、页面开着时再跳过来都停在地址上的那一项。
 */
export function financePaymentRequestListSearchParams(scope: FinancePaymentRequestListScope): URLSearchParams {
  return new URLSearchParams(scope === "all" ? {} : { scope });
}

/** 从地址读请款记录的筛选；没写或不认识的值按「全部」。 */
export function readFinancePaymentRequestListScope(params: URLSearchParams): FinancePaymentRequestListScope {
  const scope = params.get("scope");
  return scope !== null && scope in LIST_SCOPE_LABELS ? (scope as FinancePaymentRequestListScope) : "all";
}

/** 请款记录的筛选选项，按页面上的顺序。 */
export const FINANCE_PAYMENT_REQUEST_LIST_SCOPE_OPTIONS = (
  Object.keys(LIST_SCOPE_LABELS) as FinancePaymentRequestListScope[]
).map((value) => ({ value, label: LIST_SCOPE_LABELS[value] }));

/** 款项类型选项，按表单上的顺序。 */
export const FINANCE_PAYMENT_TYPE_OPTIONS = (Object.keys(PAYMENT_TYPE_LABELS) as FinancePaymentType[]).map(
  (value) => ({ value, label: PAYMENT_TYPE_LABELS[value] }),
);

/** 票据状态选项，按表单上的顺序。 */
export const FINANCE_INVOICE_STATUS_OPTIONS = (Object.keys(INVOICE_STATUS_LABELS) as FinanceInvoiceStatus[]).map(
  (value) => ({ value, label: INVOICE_STATUS_LABELS[value] }),
);

/** 款项类型的中文名。 */
export function financePaymentTypeLabel(value: FinancePaymentType): string {
  return PAYMENT_TYPE_LABELS[value];
}

/** 票据状态的中文名。 */
export function financeInvoiceStatusLabel(value: FinanceInvoiceStatus): string {
  return INVOICE_STATUS_LABELS[value];
}

/** 请款状态的中文名。 */
export function financePaymentRequestStatusLabel(value: FinancePaymentRequestStatus): string {
  return STATUS_LABELS[value];
}

/** 请款记录「是否已付」一列：有有效已付登记（状态为已付）写「是」，其余写「否」。 */
export function financePaymentRequestPaidLabel(status: FinancePaymentRequestStatus): string {
  return status === "paid" ? "是" : "否";
}

/** 能不能登记已付：只有审批已通过、还没有有效登记（状态还是已通过、不是已付）的请款能登记。 */
export function canRecordFinancePaymentRequestPayment(status: FinancePaymentRequestStatus): boolean {
  return status === "approved";
}

/** 能不能撤回：申请人本人、审批中；第一级有没有人处理过由后端（审批模块）判断，拦下时原样提示。 */
export function canWithdrawFinancePaymentRequest(detail: FinancePaymentRequestDetail, userId: number | undefined): boolean {
  return detail.applicantId === userId && detail.status === "pending";
}

/** 能不能修改后重新提交：申请人本人、被驳回或撤回之后；审批中的要先撤回，已通过、已付的不能改。 */
export function canResubmitFinancePaymentRequest(detail: FinancePaymentRequestDetail, userId: number | undefined): boolean {
  return detail.applicantId === userId && (detail.status === "rejected" || detail.status === "withdrawn");
}

/**
 * 修改后重新提交：团和结算主体从原单带出（不能改），表单回填原值。
 * 页面上只有脱敏账号，手填的账户没法原样回填，所以收款账户默认「沿用原收款账户」；户名、开户行、支行回填好，
 * 切到手填时只要重填账号。附件回填原来的，可以移除、再传。
 */
export function financePaymentRequestResubmitFrom(detail: FinancePaymentRequestDetail): {
  target: FinancePaymentRequestTarget;
  resubmit: FinancePaymentRequestResubmit;
} {
  return {
    target: {
      itineraryId: detail.itineraryId,
      itineraryTitle: detail.itineraryTitle,
      settlementSupplierId: detail.settlementSupplierId,
      settlementSupplierName: detail.settlementSupplierName,
    },
    resubmit: {
      requestId: detail.requestId,
      initial: {
        paymentType: detail.paymentType,
        amount: detail.amount,
        expectedPaymentDate: detail.expectedPaymentDate,
        invoiceStatus: detail.invoiceStatus,
        payeeMode: "original",
        payeeSourceAccountId: null,
        payeeAccountName: detail.payeeAccountName,
        payeeBankName: detail.payeeBankName,
        payeeBankBranchName: detail.payeeBankBranchName ?? "",
        payeeAccountNo: "",
        purpose: detail.purpose,
        departmentId: detail.departmentId,
        attachments: detail.attachments,
        lineIds: detail.lines.map((item) => item.lineId),
        remark: detail.remark ?? "",
      },
      originalPayee: [
        detail.payeeAccountName,
        formatFinancePaymentRequestBank(detail.payeeBankName, detail.payeeBankBranchName),
        detail.payeeAccountNoMasked,
      ].join(" · "),
    },
  };
}

/** 一份空的已付登记草稿。 */
export function emptyFinancePaymentRecordDraft(): FinancePaymentRecordDraft {
  return { payerAccount: "", paymentDate: "", bankSerialNo: "", attachments: [], remark: "" };
}

/** 提交前校验已付登记草稿，返回全部错误（没有错误为空数组）；后端还会再校验一遍。备注可以不填。 */
export function validateFinancePaymentRecordDraft(draft: FinancePaymentRecordDraft): string[] {
  const errors: string[] = [];
  if (!draft.payerAccount.trim()) errors.push("请填写本方付款账户。");
  if (!draft.paymentDate) errors.push("请选择付款日期。");
  if (!draft.bankSerialNo.trim()) errors.push("请填写银行流水号。");
  if (draft.attachments.length === 0) errors.push("请至少上传 1 个付款回单。");
  return errors;
}

/** 作废原因必填，只有空白当没填；没问题返回空字符串。 */
export function validateFinancePaymentVoidReason(reason: string): string {
  return reason.trim() ? "" : "请填写作废原因。";
}

/** 一份空草稿，部门预先选好（主部门或第一个）。 */
export function emptyFinancePaymentRequestDraft(departmentId: number | null): FinancePaymentRequestDraft {
  return {
    paymentType: "",
    amount: "",
    expectedPaymentDate: "",
    invoiceStatus: "",
    payeeMode: null,
    payeeSourceAccountId: null,
    payeeAccountName: "",
    payeeBankName: "",
    payeeBankBranchName: "",
    payeeAccountNo: "",
    purpose: "",
    departmentId,
    attachments: [],
    lineIds: [],
    remark: "",
  };
}

/**
 * 收款账户怎么填。没手动切过时：有已存账户就选已存的，并预选供应商设的默认收款账户（没设默认就等人选）；
 * 一个已存账户都没有只能手填。手填时不带账户 ID。选过的账户不在这些已存账户里（预付款换了结算主体）时当没选过。
 * 沿用原收款账户（修改后重新提交时）不看已存账户、不带账户 ID。
 */
export function resolveFinancePaymentRequestPayee(
  draft: FinancePaymentRequestDraft,
  accounts: FinancePaymentRequestPayeeAccount[],
): Pick<FinancePaymentRequestDraft, "payeeMode" | "payeeSourceAccountId"> {
  if (draft.payeeMode === "original") return { payeeMode: "original", payeeSourceAccountId: null };
  const payeeMode = accounts.length > 0 ? (draft.payeeMode ?? "saved") : "manual";
  if (payeeMode === "manual") return { payeeMode, payeeSourceAccountId: null };
  const picked = accounts.find((item) => item.accountId === draft.payeeSourceAccountId);
  return {
    payeeMode,
    payeeSourceAccountId: (picked ?? accounts.find((item) => item.isDefault))?.accountId ?? null,
  };
}

/** 下拉里的一个已存收款账户：户名、开户行（含支行）、脱敏账号，默认的注明。 */
export function formatFinancePaymentRequestPayeeAccount(account: FinancePaymentRequestPayeeAccount): string {
  const label = `${account.accountName} · ${formatFinancePaymentRequestBank(account.bankName, account.bankBranchName)} · ${account.accountNoMasked}`;
  return account.isDefault ? `${label}（默认）` : label;
}

/** 勾的行的合计（两位小数字符串），按分相加不经过浮点数；一行没勾为空。 */
export function financePaymentRequestLineTotal(lines: FinancePaymentRequestLine[], lineIds: string[]): string | null {
  const picked = lines.filter((item) => lineIds.includes(item.lineId));
  if (picked.length === 0) return null;
  return feeMoneyText(picked.reduce((sum, item) => sum + (feeMoneyCents(item.amount) ?? 0n), 0n));
}

/**
 * 改了勾选之后的请款金额：金额还没填、或还等于原来的勾选合计时，跟着变成新的合计（全部取消就清空）；
 * 手改过的金额不动，金额不必等于勾选合计。
 */
export function financePaymentRequestAmountAfterTicking(
  amount: string,
  lines: FinancePaymentRequestLine[],
  previousLineIds: string[],
  nextLineIds: string[],
): string {
  const previous = financePaymentRequestLineTotal(lines, previousLineIds);
  const follows =
    amount.trim() === "" || (previous !== null && feeMoneyCents(amount.trim()) === feeMoneyCents(previous));
  return follows ? (financePaymentRequestLineTotal(lines, nextLineIds) ?? "") : amount;
}

/** 勾的行里只留现在还能勾的（修改后重新提交时回填的行可能已经不在确认件里了），按可勾的行的顺序。 */
export function financePaymentRequestTickableLineIds(lineIds: string[], lines: FinancePaymentRequestLine[]): string[] {
  return lines.filter((item) => lineIds.includes(item.lineId)).map((item) => item.lineId);
}

/** 默认选主部门；没有主部门选第一个；一个都没有为空。 */
export function defaultFinancePaymentRequestDepartmentId(departments: FinancePaymentRequestDepartment[]): number | null {
  return (departments.find((item) => item.isMain) ?? departments[0])?.deptId ?? null;
}

/**
 * 提交前校验请款草稿，返回全部错误（没有错误为空数组）；后端还会再校验一遍。
 * 金额按分比较，不经过浮点数；刚好等于可请额度可以。选已存收款账户时只要求选了一个，手填时户名、开户行、账号必填，
 * 沿用原收款账户时不用填。
 * hasDepartments 为假时只提示先去设置部门。
 * 预付款备注必填；预付款且这个团里这个结算主体还没有确认协议（hasAgreement 为假）时不受可请额度限制。
 */
export function validateFinancePaymentRequestDraft(
  draft: FinancePaymentRequestDraft,
  availableAmount: string,
  hasDepartments: boolean,
  hasAgreement = true,
): string[] {
  if (!hasDepartments) return ["你还没有归属部门，请先在「组织和用户」里给你设置部门。"];
  const errors: string[] = [];
  const prepayment = draft.paymentType === "prepayment";
  if (!draft.paymentType) errors.push("请选择款项类型。");
  errors.push(...amountErrors(draft.amount, prepayment && !hasAgreement ? null : availableAmount));
  if (!draft.expectedPaymentDate) errors.push("请选择期望付款日期。");
  if (!draft.invoiceStatus) errors.push("请选择票据状态。");
  if (draft.payeeMode === "saved") {
    if (!draft.payeeSourceAccountId) errors.push("请选择收款账户。");
  } else if (draft.payeeMode !== "original") {
    if (!draft.payeeAccountName.trim()) errors.push("请填写收款户名。");
    if (!draft.payeeBankName.trim()) errors.push("请填写开户行。");
    if (!draft.payeeAccountNo.trim()) errors.push("请填写收款账号。");
  }
  if (!draft.purpose.trim()) errors.push("请填写请款用途。");
  if (draft.departmentId === null) errors.push("请选择所选部门。");
  if (draft.attachments.length === 0) errors.push("请至少上传 1 个附件。");
  if (prepayment && !draft.remark.trim()) errors.push("预付款请填写备注，写明为什么要先付。");
  return errors;
}

/** 金额：数字、最多两位小数、大于 0、不超过可请额度（availableAmount 为空表示不受额度限制）。 */
function amountErrors(amount: string, availableAmount: string | null): string[] {
  const cents = feeMoneyCents(amount);
  if (cents === null) return ["请款金额要填数字，最多两位小数。"];
  if (cents <= 0n) return ["请款金额要大于 0。"];
  if (availableAmount === null) return [];
  const available = feeMoneyCents(availableAmount) ?? 0n;
  if (available <= 0n) return ["这个结算主体已经没有可请额度了。"];
  if (cents > available) return [`超过可请额度，最多还能请 ${formatFeeAdjustmentAmount(availableAmount, false)}。`];
  return [];
}

const ATTACHMENT_EXTENSIONS = /\.(pdf|png|jpe?g)$/iu;
const ATTACHMENT_TYPES = new Set(["application/pdf", "image/png", "image/jpeg"]);

/** 选附件时先在前端拦一遍：PDF、PNG、JPEG（按类型或扩展名），不超过 20 MB，不能是空文件；后端按文件头再认一次。 */
export function validateFinancePaymentRequestAttachment(file: File): string {
  if (!ATTACHMENT_TYPES.has(file.type) && !ATTACHMENT_EXTENSIONS.test(file.name)) return "只支持 PDF、PNG、JPEG 文件。";
  if (file.size === 0) return "文件是空的，请重新选择。";
  if (file.size > FINANCE_PAYMENT_REQUEST_MAX_ATTACHMENT_BYTES) return "单个附件不能超过 20 MB。";
  return "";
}

/** 金额显示为 ¥ 加千分位两位小数，全程不经过浮点数。 */
export function formatFinancePaymentRequestAmount(value: string): string {
  return formatFeeAdjustmentAmount(value, false);
}

/** 后端北京时间显示到秒；为空显示「—」。 */
export function formatFinancePaymentRequestTime(value: string | null): string {
  return value ? value.replace("T", " ").slice(0, 19) : "—";
}

/** 当前审批人；不在审批中时为「—」。 */
export function formatFinancePaymentRequestApprovers(approvers: Array<{ name: string }>): string {
  return approvers.length > 0 ? approvers.map((item) => item.name).join("、") : "—";
}

/** 开户行和支行连成一段；没填支行只显示开户行。 */
export function formatFinancePaymentRequestBank(bankName: string, branchName: string | null): string {
  return [bankName, branchName].filter(Boolean).join(" ");
}

/** 一级的审批人；所选部门的审批人这一级注明来源。 */
export function formatFinancePaymentRequestStepApprovers(step: FinancePaymentRequestRoundStep): string {
  const names = step.approvers.map((item) => item.name).join("、");
  return step.approverType === "dept_approver" ? `${names}（所选部门的审批人）` : names;
}

/** 一级现在怎样：处理过的写谁、怎么处理、什么时候；没处理的按所在那一轮写审批中、未到这一级或未处理。 */
export function describeFinancePaymentRequestStep(
  round: FinancePaymentRequestRound,
  step: FinancePaymentRequestRoundStep,
): string {
  if (step.result) {
    return `${step.handledByName ?? "—"} ${step.result === "approved" ? "通过" : "驳回"} · ${formatFinancePaymentRequestTime(step.handledAt)}`;
  }
  if (round.status !== "pending") return "未处理";
  return step.stepNo > currentStepNo(round) ? "未到这一级" : "审批中";
}

/** 审批中那一轮现在轮到第几级：第一个还没处理的级。 */
function currentStepNo(round: FinancePaymentRequestRound): number {
  return round.steps.find((item) => item.result === null)?.stepNo ?? round.steps.length;
}
